/**
 * AnticoagScreeningPage — 5-step receptionist decision engine.
 * Implements Sections 1–5 of the Anticoagulation & Antiplatelet Management
 * Framework SOP (anticoagulation-antiplatelet-framework-v1).
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Activity, AlertTriangle, ArrowLeft, ArrowRight, CheckCircle2, ClipboardList,
  Pill, ShieldAlert, Stethoscope, Save, Sparkles, History, X,
} from 'lucide-react';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DARK = '#1f4a2e';
const ALERT = '#B91C1C';

const RISK_TONE = {
  LOW:      { bg: 'rgba(34,197,94,0.10)',  text: '#15803D', border: '#86EFAC', label: 'LOW bleeding risk' },
  MODERATE: { bg: 'rgba(234,179,8,0.12)',  text: '#854D0E', border: '#FDE68A', label: 'MODERATE bleeding risk' },
  HIGH:     { bg: 'rgba(220,38,38,0.10)',  text: '#991B1B', border: '#FCA5A5', label: 'HIGH bleeding risk' },
};

const DECISION_TONE = {
  STANDARD:           { bg: 'rgba(34,197,94,0.12)',  text: '#166534', border: '#22C55E', icon: CheckCircle2 },
  RADIOLOGIST_REVIEW: { bg: 'rgba(234,179,8,0.14)',  text: '#854D0E', border: '#EAB308', icon: AlertTriangle },
  ESCALATE:           { bg: 'rgba(220,38,38,0.12)',  text: '#991B1B', border: '#DC2626', icon: ShieldAlert },
};

const STEPS = [
  { n: 1, label: 'Patient & procedure', icon: ClipboardList },
  { n: 2, label: 'Blood thinners',      icon: Pill },
  { n: 3, label: 'Indication',          icon: Stethoscope },
  { n: 4, label: 'Recent events',       icon: History },
  { n: 5, label: 'Decision',            icon: Sparkles },
];


export default function AnticoagScreeningPage() {
  const { user } = useAuth();
  const isPM = user?.role === 'admin' || user?.role === 'superuser';
  const [config, setConfig] = useState(null);
  const [step, setStep] = useState(1);
  const [patient, setPatient] = useState({ name: '', dob: '', phone: '', medicare: '' });
  const [procedureId, setProcedureId] = useState('');
  const [medicationIds, setMedicationIds] = useState([]);
  const [indicationIds, setIndicationIds] = useState([]);
  const [recentEventIds, setRecentEventIds] = useState([]);
  const [lastDose, setLastDose] = useState('');
  const [managingDoctor, setManagingDoctor] = useState('');
  const [notes, setNotes] = useState('');
  const [result, setResult] = useState(null);
  const [submitted, setSubmitted] = useState(null);   // {screening_id, result}
  const [evaluating, setEvaluating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [recentList, setRecentList] = useState([]);

  // Load config + (if PM) recent screenings
  useEffect(() => {
    axios.get(`${API}/anticoag/config`, { withCredentials: true })
      .then(r => setConfig(r.data))
      .catch(() => setError('Failed to load configuration. Please refresh.'));
    if (isPM) {
      axios.get(`${API}/anticoag/screenings`, { withCredentials: true })
        .then(r => setRecentList(r.data || []))
        .catch(() => {});
    }
  }, [isPM]);

  // Re-run live evaluation whenever inputs change (after step 1)
  useEffect(() => {
    if (!procedureId) { setResult(null); return; }
    setEvaluating(true);
    axios.post(`${API}/anticoag/evaluate`, {
      procedure_id: procedureId,
      medication_ids: medicationIds,
      indication_ids: indicationIds,
      recent_event_ids: recentEventIds,
    }, { withCredentials: true })
      .then(r => setResult(r.data))
      .catch(() => setResult(null))
      .finally(() => setEvaluating(false));
  }, [procedureId, medicationIds, indicationIds, recentEventIds]);

  const proc = useMemo(() => config?.procedures.find(p => p.id === procedureId), [config, procedureId]);

  // No medication shortcut: skip indication/events steps' validation
  const noMedSelected = medicationIds.includes('none');

  function toggleArr(arr, val, setter, exclusive = null) {
    if (val === exclusive) {
      setter([val]);
      return;
    }
    const next = arr.includes(val) ? arr.filter(x => x !== val) : [...arr.filter(x => x !== exclusive), val];
    setter(next);
  }

  function reset() {
    setStep(1);
    setPatient({ name: '', dob: '', phone: '', medicare: '' });
    setProcedureId('');
    setMedicationIds([]); setIndicationIds([]); setRecentEventIds([]);
    setLastDose(''); setManagingDoctor(''); setNotes('');
    setResult(null); setSubmitted(null); setError('');
  }

  async function save() {
    if (!patient.name.trim()) { setError('Patient name is required.'); setStep(1); return; }
    if (!procedureId)         { setError('Procedure is required.'); setStep(1); return; }
    setSaving(true); setError('');
    try {
      const r = await axios.post(`${API}/anticoag/screenings`, {
        patient, procedure_id: procedureId,
        medication_ids: medicationIds.filter(m => m !== 'none'),
        indication_ids: indicationIds, recent_event_ids: recentEventIds,
        last_dose: lastDose, managing_doctor: managingDoctor, notes,
      }, { withCredentials: true });
      setSubmitted(r.data);
      if (isPM) {
        const list = await axios.get(`${API}/anticoag/screenings`, { withCredentials: true });
        setRecentList(list.data || []);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to save screening.');
    } finally {
      setSaving(false);
    }
  }

  function canAdvance() {
    if (step === 1) return !!patient.name.trim() && !!procedureId;
    if (step === 2) return medicationIds.length > 0;
    if (step === 3) return noMedSelected || indicationIds.length > 0;
    if (step === 4) return noMedSelected || recentEventIds.length > 0;
    return true;
  }

  if (!config) {
    return (
      <>
        <Header />
        <div className="max-w-4xl mx-auto px-6 py-12 text-center text-muted-foreground">Loading…</div>
      </>
    );
  }

  // Group procedures by risk for the picker
  const procByRisk = { LOW: [], MODERATE: [], HIGH: [] };
  config.procedures.forEach(p => procByRisk[p.risk].push(p));

  return (
    <>
      <Header />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="anticoag-screening-page">
        {/* Title */}
        <div className="mb-6 flex items-start gap-3">
          <div className="rounded-xl p-2.5 flex-none" style={{ background: 'rgba(185,28,28,0.10)' }}>
            <Activity className="w-6 h-6" style={{ color: ALERT }} strokeWidth={1.75} />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight" style={{ color: DARK }}>
              Anticoagulation Pre-Procedural Screening
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              5-step decision engine — aligned with SIR, ASRA, CIRSE, AHA/ACC, THANZ and RANZCR. Final decisions rest with the radiologist.
            </p>
          </div>
        </div>

        {/* Submitted view */}
        {submitted ? (
          <ResultCard
            result={submitted.result}
            screeningId={submitted.screening_id}
            patientName={patient.name}
            lastDose={lastDose}
            managingDoctor={managingDoctor}
            onNew={reset}
          />
        ) : (
          <>
            {/* Stepper */}
            <ol className="flex items-center gap-1 sm:gap-2 mb-6 flex-wrap" data-testid="stepper">
              {STEPS.map(s => {
                const Icon = s.icon;
                const active = step === s.n;
                const done = step > s.n;
                return (
                  <li key={s.n} className="flex items-center gap-1 sm:gap-2">
                    <button
                      type="button"
                      onClick={() => done && setStep(s.n)}
                      data-testid={`step-${s.n}`}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm border transition-all ${
                        active ? 'border-2' : 'border'
                      } ${done ? 'cursor-pointer hover:bg-accent' : ''}`}
                      style={{
                        background: active ? 'rgba(185,28,28,0.08)' : done ? 'rgba(34,197,94,0.06)' : 'transparent',
                        color: active ? ALERT : done ? '#166534' : 'var(--muted-foreground)',
                        borderColor: active ? ALERT : done ? '#22C55E' : 'var(--border)',
                        fontWeight: active ? 600 : 400,
                      }}
                    >
                      <Icon className="w-4 h-4" strokeWidth={1.75} />
                      <span className="hidden sm:inline">{s.label}</span>
                      <span className="sm:hidden">{s.n}</span>
                    </button>
                    {s.n < STEPS.length && <span className="text-muted-foreground hidden sm:inline">→</span>}
                  </li>
                );
              })}
            </ol>

            {/* Step panes */}
            <div className="rounded-xl border bg-card p-5 sm:p-7" data-testid="step-pane">
              {step === 1 && (
                <Step1
                  patient={patient} setPatient={setPatient}
                  procedureId={procedureId} setProcedureId={setProcedureId}
                  procByRisk={procByRisk}
                />
              )}
              {step === 2 && (
                <Step2
                  medications={config.medications}
                  medicationIds={medicationIds}
                  toggle={(v) => toggleArr(medicationIds, v, setMedicationIds, 'none')}
                  lastDose={lastDose} setLastDose={setLastDose}
                  managingDoctor={managingDoctor} setManagingDoctor={setManagingDoctor}
                />
              )}
              {step === 3 && (
                <Step3
                  indications={config.indications}
                  indicationIds={indicationIds}
                  toggle={(v) => toggleArr(indicationIds, v, setIndicationIds)}
                  noMedSelected={noMedSelected}
                />
              )}
              {step === 4 && (
                <Step4
                  options={config.recent_event_options}
                  recentEventIds={recentEventIds}
                  toggle={(v) => toggleArr(recentEventIds, v, setRecentEventIds, 'none')}
                  notes={notes} setNotes={setNotes}
                  noMedSelected={noMedSelected}
                />
              )}
              {step === 5 && (
                <Step5 result={result} evaluating={evaluating} proc={proc} />
              )}
            </div>

            {/* Footer nav */}
            <div className="mt-5 flex items-center justify-between" data-testid="step-nav">
              <button
                type="button"
                onClick={() => setStep(Math.max(1, step - 1))}
                disabled={step === 1}
                data-testid="back-btn"
                className="px-4 py-2 text-sm rounded-lg border hover:bg-accent disabled:opacity-40 inline-flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              {error && (
                <div className="text-sm text-red-600 inline-flex items-center gap-1" data-testid="error-banner">
                  <X className="w-4 h-4" />{error}
                </div>
              )}
              {step < 5 ? (
                <button
                  type="button"
                  onClick={() => { setError(''); setStep(step + 1); }}
                  disabled={!canAdvance()}
                  data-testid="next-btn"
                  className="px-4 py-2 text-sm rounded-lg text-white disabled:opacity-40 inline-flex items-center gap-2"
                  style={{ background: ALERT }}
                >
                  Next <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={save}
                  disabled={saving || !result}
                  data-testid="save-btn"
                  className="px-4 py-2 text-sm rounded-lg text-white disabled:opacity-40 inline-flex items-center gap-2"
                  style={{ background: DARK }}
                >
                  <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save screening'}
                </button>
              )}
            </div>
          </>
        )}

        {/* Recent screenings (PM only) */}
        {isPM && recentList.length > 0 && (
          <div className="mt-10" data-testid="recent-screenings">
            <h2 className="text-base font-semibold mb-3" style={{ color: DARK }}>Recent screenings</h2>
            <div className="rounded-xl border bg-card overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-accent/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2">Patient</th>
                    <th className="px-4 py-2">Procedure</th>
                    <th className="px-4 py-2">Decision</th>
                    <th className="px-4 py-2">Submitted</th>
                  </tr>
                </thead>
                <tbody>
                  {recentList.slice(0, 10).map(r => {
                    const tone = DECISION_TONE[r.result?.decision] || DECISION_TONE.STANDARD;
                    return (
                      <tr key={r.screening_id} className="border-t border-border">
                        <td className="px-4 py-2 font-medium">{r.patient?.name}</td>
                        <td className="px-4 py-2">{r.result?.procedure?.label}</td>
                        <td className="px-4 py-2">
                          <span className="px-2 py-0.5 rounded text-xs font-semibold"
                                style={{ background: tone.bg, color: tone.text, border: `1px solid ${tone.border}` }}>
                            {r.result?.decision_label}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-muted-foreground text-xs">
                          {new Date(r.submitted_at).toLocaleString()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Mandatory footer */}
        <p className="mt-8 text-xs text-muted-foreground italic" data-testid="mandatory-footer">
          These recommendations are intended as guidance only and do not replace individualised clinical assessment by the treating radiologist or referring clinician.
        </p>
      </div>
    </>
  );
}


// ============================================================
// Step components
// ============================================================
function Step1({ patient, setPatient, procedureId, setProcedureId, procByRisk }) {
  return (
    <div className="space-y-5">
      <h2 className="text-lg font-semibold" style={{ color: DARK }}>Step 1 · Patient & procedure</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Input label="Patient full name *" value={patient.name} onChange={v => setPatient({ ...patient, name: v })}
               testid="patient-name-input" />
        <Input label="Date of birth"      value={patient.dob}   onChange={v => setPatient({ ...patient, dob: v })}
               placeholder="DD/MM/YYYY" testid="patient-dob-input" />
        <Input label="Phone"              value={patient.phone} onChange={v => setPatient({ ...patient, phone: v })}
               testid="patient-phone-input" />
        <Input label="Medicare number"    value={patient.medicare} onChange={v => setPatient({ ...patient, medicare: v })}
               testid="patient-medicare-input" />
      </div>

      <div>
        <label className="text-sm font-medium block mb-2">Procedure *</label>
        {['LOW', 'MODERATE', 'HIGH'].map(risk => (
          <div key={risk} className="mb-3">
            <div className="text-xs font-semibold uppercase tracking-wider mb-1 px-2 py-1 rounded inline-block"
                 style={{ background: RISK_TONE[risk].bg, color: RISK_TONE[risk].text }}>
              {RISK_TONE[risk].label}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 mt-1">
              {procByRisk[risk].map(p => (
                <label key={p.id}
                       className={`flex items-start gap-2 px-3 py-2 text-sm rounded-lg border cursor-pointer hover:bg-accent ${
                         procedureId === p.id ? 'border-2' : ''
                       }`}
                       style={procedureId === p.id ? { borderColor: ALERT, background: 'rgba(185,28,28,0.04)' } : {}}>
                  <input
                    type="radio" name="procedure" value={p.id}
                    checked={procedureId === p.id}
                    onChange={() => setProcedureId(p.id)}
                    data-testid={`proc-radio-${p.id}`}
                    className="mt-0.5"
                  />
                  <span>{p.label}{p.is_spine && <span className="ml-1.5 text-[10px] font-bold text-red-700">SPINE</span>}</span>
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


function Step2({ medications, medicationIds, toggle, lastDose, setLastDose, managingDoctor, setManagingDoctor }) {
  return (
    <div className="space-y-5">
      <h2 className="text-lg font-semibold" style={{ color: DARK }}>Step 2 · Blood thinners & antiplatelets</h2>
      <p className="text-sm text-muted-foreground italic">
        "Are you taking any blood thinning medications?" — tick all that apply, or "Not on any blood thinner".
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
        <Checkbox
          label="Not on any blood thinner"
          checked={medicationIds.includes('none')}
          onChange={() => toggle('none')}
          testid="med-chk-none"
        />
        {medications.map(m => (
          <Checkbox
            key={m.id}
            label={`${m.label}`}
            sub={m.class}
            checked={medicationIds.includes(m.id)}
            onChange={() => toggle(m.id)}
            testid={`med-chk-${m.id}`}
          />
        ))}
      </div>

      {medicationIds.length > 0 && !medicationIds.includes('none') && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input label="Date / time of last dose" value={lastDose} onChange={setLastDose}
                 placeholder="e.g. This morning at 7am" testid="last-dose-input" />
          <Input label="Managing doctor (GP / specialist)" value={managingDoctor} onChange={setManagingDoctor}
                 placeholder="Name & clinic" testid="managing-doctor-input" />
        </div>
      )}
    </div>
  );
}


function Step3({ indications, indicationIds, toggle, noMedSelected }) {
  if (noMedSelected) {
    return (
      <div className="space-y-3">
        <h2 className="text-lg font-semibold" style={{ color: DARK }}>Step 3 · Indication</h2>
        <p className="text-sm text-muted-foreground">Skipped — patient is not on any blood thinner.</p>
      </div>
    );
  }
  return (
    <div className="space-y-5">
      <h2 className="text-lg font-semibold" style={{ color: DARK }}>Step 3 · Indication (why they take it)</h2>
      <p className="text-sm text-muted-foreground italic">"What is the medication for?" — tick all that apply.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
        {indications.map(i => (
          <Checkbox
            key={i.id}
            label={i.label}
            sub={i.high_risk ? 'High-risk indication' : ''}
            checked={indicationIds.includes(i.id)}
            onChange={() => toggle(i.id)}
            testid={`ind-chk-${i.id}`}
          />
        ))}
      </div>
    </div>
  );
}


function Step4({ options, recentEventIds, toggle, notes, setNotes, noMedSelected }) {
  if (noMedSelected) {
    return (
      <div className="space-y-3">
        <h2 className="text-lg font-semibold" style={{ color: DARK }}>Step 4 · Recent events</h2>
        <p className="text-sm text-muted-foreground">Skipped — patient is not on any blood thinner.</p>
        <Textarea label="Notes (optional)" value={notes} onChange={setNotes} testid="notes-input" />
      </div>
    );
  }
  return (
    <div className="space-y-5">
      <h2 className="text-lg font-semibold" style={{ color: DARK }}>Step 4 · Recent events</h2>
      <p className="text-sm text-muted-foreground italic">
        "Have you had any recent stents, strokes, blood clots or heart procedures in the past 12 months?"
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
        {options.map(o => (
          <Checkbox
            key={o.id}
            label={o.label}
            checked={recentEventIds.includes(o.id)}
            onChange={() => toggle(o.id)}
            testid={`event-chk-${o.id}`}
          />
        ))}
      </div>
      <Textarea label="Notes (optional)" value={notes} onChange={setNotes} testid="notes-input" />
    </div>
  );
}


function Step5({ result, evaluating, proc }) {
  if (evaluating || !result) {
    return <div className="text-sm text-muted-foreground">Computing decision…</div>;
  }
  return (
    <div className="space-y-5">
      <h2 className="text-lg font-semibold" style={{ color: DARK }}>Step 5 · Decision</h2>
      <DecisionBanner result={result} />
      <ReasonList reasons={result.reasons} />
      <PatientScript script={result.patient_script} />
      {result.medication_guidance.length > 0 && (
        <MedicationGuidance items={result.medication_guidance} risk={result.procedure.risk} />
      )}
      {proc?.is_spine && <SpineWarning />}
    </div>
  );
}


// ============================================================
// Decision blocks
// ============================================================
function DecisionBanner({ result }) {
  const tone = DECISION_TONE[result.decision] || DECISION_TONE.STANDARD;
  const Icon = tone.icon;
  return (
    <div className="rounded-xl border-2 p-4 sm:p-5"
         data-testid={`decision-banner-${result.decision}`}
         style={{ background: tone.bg, borderColor: tone.border, color: tone.text }}>
      <div className="flex items-start gap-3">
        <Icon className="w-6 h-6 flex-none" strokeWidth={1.75} />
        <div>
          <div className="font-semibold text-base">{result.decision_label}</div>
          <div className="text-sm opacity-90 mt-0.5">
            {result.procedure.label} · <strong>{RISK_TONE[result.procedure.risk]?.label || result.procedure.risk}</strong>
            {result.procedure.is_spine && ' · Spine / neuraxial'}
          </div>
        </div>
      </div>
    </div>
  );
}


function ReasonList({ reasons }) {
  return (
    <div data-testid="reasons-list">
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Why this decision</div>
      <ul className="space-y-1 text-sm">
        {reasons.map((r, i) => (
          <li key={i} className="flex items-start gap-2">
            <span className="text-muted-foreground mt-0.5">•</span>
            <span>{r}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}


function PatientScript({ script }) {
  return (
    <div className="rounded-lg border bg-muted/30 p-4" data-testid="patient-script">
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Script for the patient</div>
      <p className="text-sm italic">"{script}"</p>
    </div>
  );
}


function MedicationGuidance({ items, risk }) {
  return (
    <div data-testid="medication-guidance">
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
        Per-medication guidance ({risk} risk)
      </div>
      <div className="rounded-lg border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-accent/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-3 py-2">Medication</th>
              <th className="px-3 py-2">Class</th>
              <th className="px-3 py-2">Typical approach</th>
              <th className="px-3 py-2">Note</th>
            </tr>
          </thead>
          <tbody>
            {items.map(m => (
              <tr key={m.id} className="border-t border-border align-top">
                <td className="px-3 py-2 font-medium">{m.label}</td>
                <td className="px-3 py-2 text-muted-foreground">{m.class}{m.renal_dependent && <span className="block text-[10px] text-amber-700">Renal-dependent</span>}</td>
                <td className="px-3 py-2">{m.hold}</td>
                <td className="px-3 py-2 text-muted-foreground text-xs">{m.note || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function SpineWarning() {
  return (
    <div className="rounded-lg border-2 border-red-200 bg-red-50 p-3 text-sm text-red-900 flex items-start gap-2"
         data-testid="spine-warning">
      <ShieldAlert className="w-5 h-5 flex-none text-red-700" />
      <div>
        <strong>Spine / neuraxial procedure.</strong> Do not book without explicit radiologist review when the patient is on any anticoagulant or antiplatelet. ANZCA PS09 thresholds apply.
      </div>
    </div>
  );
}


function ResultCard({ result, screeningId, patientName, lastDose, managingDoctor, onNew }) {
  const tone = DECISION_TONE[result.decision] || DECISION_TONE.STANDARD;
  const Icon = tone.icon;
  return (
    <div className="space-y-5" data-testid="result-saved">
      <div className="rounded-xl border-2 p-5"
           style={{ background: tone.bg, borderColor: tone.border, color: tone.text }}>
        <div className="flex items-start gap-3">
          <Icon className="w-7 h-7 flex-none" />
          <div className="flex-1">
            <div className="text-xs uppercase tracking-wider opacity-80">Screening saved · {screeningId}</div>
            <div className="font-semibold text-lg mt-0.5">{result.decision_label}</div>
            <div className="text-sm opacity-90 mt-0.5">
              {patientName} · {result.procedure.label}
            </div>
            {lastDose && <div className="text-xs opacity-80 mt-1">Last dose: {lastDose}</div>}
            {managingDoctor && <div className="text-xs opacity-80">Managing doctor: {managingDoctor}</div>}
          </div>
        </div>
      </div>
      <PatientScript script={result.patient_script} />
      {result.medication_guidance.length > 0 && (
        <MedicationGuidance items={result.medication_guidance} risk={result.procedure.risk} />
      )}
      <button
        type="button"
        onClick={onNew}
        data-testid="new-screening-btn"
        className="px-4 py-2 text-sm rounded-lg text-white inline-flex items-center gap-2"
        style={{ background: DARK }}
      >
        New screening
      </button>
    </div>
  );
}


// ============================================================
// Atoms
// ============================================================
function Input({ label, value, onChange, placeholder, testid }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <input
        type="text"
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full mt-0.5 px-3 py-2 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2"
        style={{ '--tw-ring-color': ALERT }}
      />
    </label>
  );
}

function Textarea({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <textarea
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        data-testid={testid}
        rows={3}
        className="w-full mt-0.5 px-3 py-2 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2"
      />
    </label>
  );
}

function Checkbox({ label, sub, checked, onChange, testid }) {
  return (
    <label className={`flex items-start gap-2 px-3 py-2 text-sm rounded-lg border cursor-pointer hover:bg-accent ${
      checked ? 'border-2' : ''
    }`} style={checked ? { borderColor: ALERT, background: 'rgba(185,28,28,0.04)' } : {}}>
      <input type="checkbox" checked={checked} onChange={onChange} data-testid={testid} className="mt-0.5" />
      <span>
        <span>{label}</span>
        {sub && <span className="block text-[10px] text-muted-foreground">{sub}</span>}
      </span>
    </label>
  );
}
